import { useNavigate } from "react-router-dom";
import { isToday, isYesterday, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Bell, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNotifications } from "@/features/notifications/hooks/useNotifications";
import { NotificationItem } from "@/features/notifications/components/NotificationItem";
import type { Notification } from "@/features/notifications/types";

function groupByDate(notifications: Notification[]): Array<{ label: string; items: Notification[] }> {
  const groups = new Map<string, Notification[]>();

  for (const n of notifications) {
    const date = new Date(n.created_at);
    let label: string;
    if (isToday(date)) {
      label = "Hoje";
    } else if (isYesterday(date)) {
      label = "Ontem";
    } else {
      label = format(date, "dd 'de' MMMM", { locale: ptBR });
    }
    const existing = groups.get(label) ?? [];
    existing.push(n);
    groups.set(label, existing);
  }

  return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
}

export default function Notificacoes() {
  const { notifications, unreadCount, markRead, markAllRead, isLoading } =
    useNotifications();
  const navigate = useNavigate();

  const handleItemClick = (notification: Notification) => {
    if (!notification.read) markRead(notification.id);
    if (notification.href) navigate(notification.href);
  };

  const grouped = groupByDate(notifications);

  return (
    <div className="w-full py-8 px-4 animate-fade-in">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Notificações</h1>
          {unreadCount > 0 && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {unreadCount} não {unreadCount === 1 ? "lida" : "lidas"}
            </p>
          )}
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" onClick={markAllRead} className="gap-2">
            <CheckCircle weight="regular" className="w-4 h-4" aria-hidden />
            Marcar todas como lidas
          </Button>
        )}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="h-16 rounded-lg bg-muted/40 animate-pulse"
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && notifications.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
            <Bell weight="light" className="w-8 h-8 text-muted-foreground/60" />
          </div>
          <h3 className="text-base font-medium mb-1">Tudo em dia</h3>
          <p className="text-sm text-muted-foreground">
            Você receberá notificações aqui quando análises forem concluídas e
            outras ações importantes acontecerem.
          </p>
        </div>
      )}

      {/* Grouped notifications */}
      {!isLoading && grouped.length > 0 && (
        <div className="space-y-6">
          {grouped.map(({ label, items }) => (
            <section key={label}>
              <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
                {label}
              </h2>
              <div
                className={cn(
                  "rounded-xl border border-border overflow-hidden divide-y divide-border/60",
                  "bg-card",
                )}
              >
                {items.map((n) => (
                  <NotificationItem key={n.id} notification={n} onClick={handleItemClick} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
